import React, { useEffect, useState } from "react";
import { isZh } from "../utils/language";
import { Link } from "react-router-dom";

import PERMISSIONS, { apiFetch, hasUmatPermission, getJubileesCached } from "../utils/wkCommon";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";
import { getUser } from "../utils/auth";

import { toPinyinSync, capitalizePinyin } from "../utils/pinyinUtil";
import { getYearGanZhiByPreciseLiChun } from "../utils/lunarGanZhi";
import { translateSubject } from "../components/CardJubilee";
import DetailAgenda from "../components/DetailAgenda";

/* =========================
   DAY FORMATTERS
========================= */

const ID_DAYS = ["Min","Sen","Sel","Rab","Kam","Jum","Sab"];
const ZH_DAYS = ["週日","週一","週二","週三","週四","週五","週六"];

function formatDateID(dateStr){
  const d = new Date(dateStr);
  const wd = ID_DAYS[d.getDay()];
  const day = d.getDate().toString().padStart(2,"0");
  const mon = d.toLocaleDateString("id-ID",{month:"short"});
  return `${wd} ${day}-${mon}`;
}

function formatDateZH(dateStr){
  const d = new Date(dateStr);
  const wd = ZH_DAYS[d.getDay()];
  const m = d.getMonth()+1;
  const day = d.getDate();
  return `${m}月${day}日  ${wd}`;
}

/* =========================
   PERMISSION
========================= */

function useAgendaPermissions(){

  const user = getUser();
  const email = user?.email ?? null;

  const quickPresensi =
    hasUmatPermission(email, PERMISSIONS.EVENTPARTICIPANT_READ);

  const [canPresensi,setCanPresensi] = useState(Boolean(quickPresensi));

  const [canAgenda,setCanAgenda] = useState(false);

  useEffect(()=>{

    let mounted=true;

    async function load(){

      try{

        if(!quickPresensi){
          const ok = await hasUmatPermissionAsync(
            email,
            PERMISSIONS.EVENTPARTICIPANT_READ
          );
          if(mounted) setCanPresensi(Boolean(ok));
        }

        const a = await hasUmatPermissionAsync(
          email,
          PERMISSIONS.AGENDA_RUNDOWN_READ
        );

        const b = await hasUmatPermissionAsync(
          email,
          PERMISSIONS.AGENDA_PERSONNEL_READ
        );

        const c = await hasUmatPermissionAsync(
          email,
          PERMISSIONS.AGENDA_EXPENSE_READ
        );

        if(mounted)
          setCanAgenda(Boolean(a||b||c));

      }catch(e){
        console.warn("perm load failed",e);
      }

    }

    load();

    return ()=>{mounted=false};

  },[email]);

  return {canPresensi,canAgenda};

}

/* =========================
   JUBILEE TRANSLATION
========================= */

const XIANGONG_MAP = {
  五盤: "5 Piring",
  廿五盤: "25 Piring",
};

const TATAKRAMA_MAP = {
  五叩首: "5x Koushou",
  初一十五: "Tata Krama Chuyi Shiwu",
  初一十五禮: "Tata Krama Chuyi Shiwu",
  三跪九叩: "San Gui Jiu Kou",
  三叩首: "3x Koushou",
  九五大禮初一十五禮: "Jiu Wu Da Li + Chuyi Shiwu",
  辭歲拜年: "Cisui Bainian",
};

function translateXianGong(t){
  if(!t) return "";
  if(isZh()) return t;
  if(XIANGONG_MAP[t]) return XIANGONG_MAP[t];
  return capitalizePinyin(toPinyinSync(t));
}

function translateTataKrama(t){
  if(!t) return "";
  if(isZh()) return t;
  if(TATAKRAMA_MAP[t]) return TATAKRAMA_MAP[t];
  return capitalizePinyin(toPinyinSync(t));
}

/* =========================
   COMPONENT
========================= */

export default function FocusedEventsPanel(){

  const [rows,setRows] = useState([]);
	const [loading,setLoading] = useState(true);

	const [showAll,setShowAll] = useState(false);
  const [isMobile,setIsMobile] = useState(false);

  const {canPresensi,canAgenda} = useAgendaPermissions();

  const [selectedAgenda, setSelectedAgenda] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(()=>{
    function check(){
      setIsMobile(window.innerWidth < 900);
    }
    check();
    window.addEventListener("resize",check);
    return ()=>window.removeEventListener("resize",check);
  },[]);

  useEffect(()=>{

    async function load(){

      const today = new Date();

      const agendaEnd = new Date(today);
      agendaEnd.setDate(today.getDate()+7);

      const jubEnd = new Date(today);
      jubEnd.setDate(today.getDate()+7);

			const qsLunar = new URLSearchParams();
			qsLunar.append("filters[dateInt][$gte]", today.toISOString());
			qsLunar.append("filters[dateInt][$lte]", jubEnd.toISOString());

			const lunarResp = await apiFetch(`/api/lunar-dates?${qsLunar.toString()}`);

			const lunarMap = {};

			(lunarResp?.data || []).forEach(it => {
				const r = it.attributes ?? it;
				const key = r.dateInt.slice(0,10);

				lunarMap[key] = {
					...r,
					lunarGanZhi: getYearGanZhiByPreciseLiChun(key)
				};
			});

			/* ========= agendas ========= */

      const qs = new URLSearchParams();

      qs.append("filters[agendaDate][$gte]",today.toISOString());
      qs.append("filters[agendaDate][$lte]",agendaEnd.toISOString());

      //qs.append("populate[0]","area");
      //qs.append("populate[1]","area.region");
      qs.append("fields[0]", "agendaDate");
      qs.append("fields[1]", "subject");
      qs.append("fields[2]", "subject_zh");
      qs.append("fields[3]", "documentId");
      qs.append("fields[4]", "externalDanWeiOtherDesc");

      qs.append("populate[fotang_external][fields][0]", "fotangNameCH");
      qs.append("populate[fotang_external][fields][1]", "fotangNamePinyin");
      qs.append("populate[area][fields][0]", "shortname");
      qs.append("populate[area][populate][region][fields][0]", "outerRegion");

      const ag = await apiFetch(`/api/agendas?${qs.toString()}`);

      const agendas = (ag?.data || []).map(a=>{
        const ev = a.attributes ?? a;

        return {
          type:"agenda",
          date:ev.agendaDate.slice(0,10),
          ev
        };
      });

      /* ========= jubilees ========= */

      const jres = await getJubileesCached();
			const jubilees = [];
			const allJub = (jres?.data || []).map(j=> j.attributes ?? j);

			for(let d=new Date(today); d<=jubEnd; d.setDate(d.getDate()+1)){

				const current = new Date(d);
				const key = current.toISOString().slice(0,10);

				const gm = current.getMonth()+1;
				const gd = current.getDate();

				let hasBigFestival = false;

				/* ===== cek festival Gregorian ===== */

				allJub.forEach(j=>{

					if(!j.jubileeLunar &&
						j.jubileeMonth===gm &&
						j.jubileeDay===gd){

						hasBigFestival = true;

						jubilees.push({
							type:"jubilee",
							date:key,
							jub:j
						});

					}

				});

				/* ===== cek festival lunar ===== */

				const lunar = lunarMap?.[key];

				if(lunar && !lunar.leapMonth){

					allJub.forEach(j=>{

						if(j.jubileeLunar &&
							j.jubileeMonth===lunar.lunarMonth &&
							j.jubileeDay===lunar.lunarDay){

							hasBigFestival = true;

							jubilees.push({
								type:"jubilee",
								date:key,
								jub:j
							});

						}

					});

				}

				/* ===== auto 初一 / 十五 ===== */

				if(lunar && (lunar.lunarDay===1 || lunar.lunarDay===15)){

					if(
						(lunar.lunarMonth===1 && lunar.lunarDay===1) ||
						(lunar.lunarMonth===3 && lunar.lunarDay===15) ||
						(lunar.lunarMonth===6 && lunar.lunarDay===15) ||
						(lunar.lunarMonth===7 && lunar.lunarDay===15) ||
						(lunar.lunarMonth===8 && lunar.lunarDay===15) ||
						(lunar.lunarMonth===9 && lunar.lunarDay===15) ||
						(lunar.lunarMonth===11 && lunar.lunarDay===15)
					){
						/* skip festival besar */
					}
					else if(!hasBigFestival){

						jubilees.push({
							type:"jubilee",
							date:key,
							jub:{
								jubileeDesc: lunar.lunarDay==1 ? "農曆初一" : "農曆十五",
								xianGong:"五盤",
								tataKrama:"初一十五禮"
							}
						});

					}

				}

			} 

      const merged = [...agendas,...jubilees];

      merged.sort((a,b)=>a.date.localeCompare(b.date));

      setRows(merged.slice(0,10));

      setLoading(false);
    }

    load();

  },[]);

  if(loading){

    return(
      <div className="focused-panel">
        <div className="focused-loading">
          Loading...
        </div>
      </div>
    );

  }

  const todayKey =
    new Date().toISOString().slice(0,10);

  const visibleRows =
    isMobile && !showAll
      ? rows.slice(0,3)
      : rows;

  async function fetchAgendaDetail(documentId){
    if(!documentId) return null;

    setLoadingDetail(true);

    try{

      const qs = new URLSearchParams();

      qs.append("populate[0]", "area");
      qs.append("populate[1]", "area.region");
      qs.append("populate[2]", "fotang_external");
      qs.append("populate[3]", "agenda_master");
      qs.append("populate[4]", "category_age_groups");

      // 🔥 ini kunci (cukup ini saja)
      qs.append("populate[5]", "agenda_book");
      qs.append("populate[6]", "agenda_pre_reg_form");

      const url = `/api/agendas/${encodeURIComponent(documentId)}?${qs.toString()}`;

      const res = await apiFetch(url);

      // strapi v5: data langsung object
      return res?.data ?? res;

    }catch(e){
      console.error("fetch detail failed", e);
      return null;
    }finally{
      setLoadingDetail(false);
    }
  }

  return (
    <>
    <style>{`
      /* ===== ROOT ===== */
      .fea-modal-root {
        position: relative;
        z-index: 1000;
      }

      /* ===== OVERLAY ===== */
      .fea-modal-root .fea-modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.4);

        display: flex;
        justify-content: center;
        align-items: flex-start;

        padding-top: 1rem;
      }

      /* ===== CONTENT ===== */
      .fea-modal-root .fea-modal-content {
        background: #fff;

        width: 95%;
        max-width: 42rem;

        max-height: 90vh;
        overflow-y: auto;

        border-radius: 0.75rem;
        padding: 1rem;

        box-sizing: border-box;
      }

      /* reset children */
      .fea-modal-root .fea-modal-content * {
        box-sizing: border-box;
      }

      /* ===== DETAIL PANEL ADAPTATION ===== */
      .fea-modal-root .fea-modal-content .detail-panel {
        border-radius: 0;
        border: none;
        box-shadow: none;
        padding: 0;
      }

      /* ===== MOBILE ===== */
      @media (max-width: 768px) {

        .fea-modal-root .fea-modal-overlay {
          padding: 1rem 0.5rem;
        }

        .fea-modal-root .fea-modal-content {
          background: transparent;
          width: 100%;
          max-width: 100%;
          border-radius: 0.75rem;
        }

      }
      `}</style>
    <div className="focused-panel">

      {visibleRows.map((r,i)=>{

        const alt = i%2===1;

        const dateLabel =
          isZh()
            ? formatDateZH(r.date)
            : formatDateID(r.date);

        if(r.type==="agenda"){

          const ev = r.ev;

          const areaLabel =
            ev?.area?.shortname ||
            ev?.fotang_external?.fotangNameCH ||
            ev?.externalDanWeiOtherDesc;

          let areaCat = 1;

          if(ev?.area?.region?.outerRegion===true)
            areaCat=2;

          if(!ev?.area && areaLabel)
            areaCat=3;

          const subject =
            isZh()
              ? ev.subject_zh
              : ev.subject;

          const documentId = ev.documentId;

          const isToday =
            r.date===todayKey;

          let link = null;
          let onClick = null;

          if (isToday && canPresensi) {
            link = `/presensi/${encodeURIComponent(documentId)}`;
          } else if (!isToday && canAgenda) {
            onClick = async () => {
              const full = await fetchAgendaDetail(documentId);

              if(full){
                setSelectedAgenda(full);
                setShowDetail(true);
              }
            };
          }

          return(

            <div
              key={i}
              className={`focused-row ${alt?"alt":""}`}
            >

              <div className="focused-date">
                {dateLabel}
              </div>

              <div className="focused-main">

                {areaLabel && (
                  <span className={`area-badge area-cat${areaCat}`}>
                    {isZh()
                      ? areaLabel
                      : capitalizePinyin(toPinyinSync(areaLabel))}
                  </span>
                )}

                {link ? (
                  <Link to={link} className="focused-link">
                    {subject}
                  </Link>
                ) : onClick ? (
                  <span
                    className="focused-link"
                    style={{ cursor: "pointer" }}
                    onClick={onClick}
                  >
                    {subject}
                  </span>
                ) : (
                  <span>{subject}</span>
                )}

              </div>

            </div>

          );

        }

        if(r.type==="jubilee"){

          const j = r.jub;

          const xiangong = translateXianGong(j.xianGong);
          const tatakrama = translateTataKrama(j.tataKrama);

          return(

            <div
              key={i}
              className={`focused-row jubilee ${alt?"alt":""}`}
            >

              <div className="focused-date">
                {dateLabel}
              </div>

              <div className="focused-main">

                <span className="jub-label">
                  Jubilee
                </span>

								<span
									dangerouslySetInnerHTML={{
										__html: isZh()
											? j.jubileeDesc
											: translateSubject(j.jubileeDesc)
									}}
								/>
                <div className="jub-meta">
                  {xiangong} <span style={{ paddingLeft: "0.3rem" }}>{tatakrama}</span>
                </div>

              </div>

            </div>

          );

        }

        return null;

      })}

      {isMobile && rows.length>3 && !showAll && (

        <div
          className="focused-more"
          onClick={()=>setShowAll(true)}
        >
          lihat lainnya...
        </div>

      )}

    </div>
    {showDetail && (
      <div className="fea-modal-root">
        <div className="fea-modal-overlay">
          <div className="fea-modal-content" 
            style={{
              "--right-w": "auto"
            }}
          >

            {loadingDetail ? (
              <div style={{ padding: "1rem" }}>Loading detail...</div>
            ) : (
              <div className="detail-panel desktop open">
              <DetailAgenda
                selectedAgenda={selectedAgenda}
                onClose={() => setShowDetail(false)}
              />
              </div>
            )}

          </div>
        </div>
      </div>
    )}
    </>
  );

}